import maplibregl from 'maplibre-gl'
import Supercluster from 'supercluster'
import {LngLat} from './route-geometry'

/**
 * Clusters the live vehicle HTML markers on the map. MapLibre's native clustering
 * works on GeoJSON sources only, so for the rich HTML markers we run supercluster
 * ourselves: nearby vehicles collapse into a count bubble, the manager just decides
 * which individual markers (owned by Map.vue) are shown, hidden or fanned out.
 */
export interface VehicleClusterHost {
  /** Add the vehicle marker to the map; lngLat overrides its position (spiderfy fan-out). */
  showVehicle(index: number, lngLat?: LngLat): void
  hideVehicle(index: number): void
  /** Move the marker back to its real position after a spiderfy. */
  restoreVehicle(index: number): void
}

export interface VehiclePoint {
  index: number
  lngLat: LngLat
}

type PointProps = {index: number}

const LEGS_SOURCE = 'vehicle-cluster-legs'
// Smaller radius = clusters break apart into individual vehicles sooner when zooming in.
const SC_RADIUS = 40
const SC_MAX_ZOOM = 16

export class VehicleClusterManager {
  private readonly map: maplibregl.Map
  private readonly host: VehicleClusterHost
  private readonly index: Supercluster<PointProps>
  private indices: number[] = []
  private clusterMarkers: maplibregl.Marker[] = []
  private spiderIndices: number[] = []
  private enabled = true
  private legColor = '#888'
  private readonly onMoveEnd = () => this.render()
  private readonly onMoveStart = () => this.closeSpider()

  constructor(map: maplibregl.Map, host: VehicleClusterHost) {
    this.map = map
    this.host = host
    this.index = new Supercluster<PointProps>({radius: SC_RADIUS, maxZoom: SC_MAX_ZOOM})
  }

  /** Adds the spider-leg layer and viewport listeners. Call once after map load. */
  init(): void {
    this.legColor = readCssVar('--primary-color') || this.legColor

    if (!this.map.getSource(LEGS_SOURCE)) {
      this.map.addSource(LEGS_SOURCE, {type: 'geojson', data: emptyFeatureCollection()})
      this.map.addLayer({
        id: LEGS_SOURCE,
        type: 'line',
        source: LEGS_SOURCE,
        paint: {'line-color': this.legColor, 'line-width': 1.5, 'line-opacity': 0.7}
      })
    }

    this.map.on('moveend', this.onMoveEnd)
    this.map.on('movestart', this.onMoveStart)
  }

  /** Refreshes the clustered point set (called on every data refresh) and re-renders. */
  update(points: VehiclePoint[]): void {
    this.indices = points.map((p) => p.index)
    this.index.load(points.map((p) => ({
      type: 'Feature',
      properties: {index: p.index},
      geometry: {type: 'Point', coordinates: p.lngLat}
    })) as any)
    this.closeSpider()
    this.render()
  }

  enable(): void {
    this.enabled = true
    this.render()
  }

  /** Hides clusters and stops clustering (used when a single vehicle is selected). */
  disable(): void {
    this.enabled = false
    this.closeSpider()
    this.clearClusters()
  }

  destroy(): void {
    this.map.off('moveend', this.onMoveEnd)
    this.map.off('movestart', this.onMoveStart)
    this.closeSpider()
    this.clearClusters()
    if (!this.map.style) return
    if (this.map.getLayer(LEGS_SOURCE)) this.map.removeLayer(LEGS_SOURCE)
    if (this.map.getSource(LEGS_SOURCE)) this.map.removeSource(LEGS_SOURCE)
  }

  private render(): void {
    if (!this.enabled || !this.map.style) return
    this.clearClusters()

    const bounds = this.map.getBounds()
    const clusters = this.index.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      Math.round(this.map.getZoom())
    )

    const visible = new Set<number>()
    clusters.forEach((feature: any) => {
      const lngLat = feature.geometry.coordinates as LngLat
      if (feature.properties.cluster) {
        this.addClusterMarker(feature.properties.point_count, feature.properties.cluster_id, lngLat)
      } else {
        visible.add(feature.properties.index)
        this.host.showVehicle(feature.properties.index)
      }
    })

    this.indices.forEach((index) => {
      if (!visible.has(index)) this.host.hideVehicle(index)
    })
  }

  private addClusterMarker(count: number, clusterId: number, lngLat: LngLat): void {
    const element = document.createElement('div')
    element.className = 'veh-cluster'
    if (count >= 25) element.classList.add('veh-cluster--lg')
    else if (count >= 10) element.classList.add('veh-cluster--md')
    element.textContent = String(count)

    element.addEventListener('click', (event) => {
      event.stopPropagation()
      this.onClusterClick(clusterId, lngLat)
    })

    this.clusterMarkers.push(new maplibregl.Marker({element}).setLngLat(lngLat).addTo(this.map))
  }

  private onClusterClick(clusterId: number, center: LngLat): void {
    this.closeSpider()
    const expansionZoom = this.index.getClusterExpansionZoom(clusterId)

    // Zooming in will split the cluster; if it cannot (vehicles share one point, e.g. a depot) fan them out instead.
    if (expansionZoom <= SC_MAX_ZOOM && expansionZoom > this.map.getZoom() + 0.3) {
      this.map.easeTo({center, zoom: expansionZoom})
    } else {
      this.spiderfy(clusterId, center)
    }
  }

  /** Fans co-located vehicles out around the cluster centre with leader lines so each is clickable. */
  private spiderfy(clusterId: number, center: LngLat): void {
    const leaves = this.index.getLeaves(clusterId, Infinity)
    const centerPx = this.map.project(center)
    const count = leaves.length
    const radius = 48 + count * 3.5
    const legs: any[] = []

    leaves.forEach((leaf, i) => {
      const angle = (i / count) * 2 * Math.PI
      const point = this.map.unproject([centerPx.x + radius * Math.cos(angle), centerPx.y + radius * Math.sin(angle)])
      const lngLat: LngLat = [point.lng, point.lat]
      const index = leaf.properties.index

      this.spiderIndices.push(index)
      this.host.showVehicle(index, lngLat)
      legs.push({type: 'Feature', properties: {}, geometry: {type: 'LineString', coordinates: [center, lngLat]}})
    })

    this.setLegs(legs)
  }

  private closeSpider(): void {
    if (!this.spiderIndices.length) return
    this.spiderIndices.forEach((index) => this.host.restoreVehicle(index))
    this.spiderIndices = []
    this.setLegs([])
  }

  private clearClusters(): void {
    this.clusterMarkers.forEach((marker) => marker.remove())
    this.clusterMarkers = []
  }

  private setLegs(features: any[]): void {
    const source = this.map.getSource(LEGS_SOURCE) as maplibregl.GeoJSONSource | undefined
    source?.setData({type: 'FeatureCollection', features} as any)
  }
}

function emptyFeatureCollection(): any {
  return {type: 'FeatureCollection', features: []}
}

/** Reads a global CSS custom property (theme primary colour) for the leg lines. */
function readCssVar(name: string): string {
  const root = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return root || getComputedStyle(document.body).getPropertyValue(name).trim()
}
